/**
 * Customers.js
 * Gestão de Clientes — Mensalistas e Credenciados
 * CI Sistemas de Informática Ltda. — FastALPR Parking — Fase 2
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiUser, FiTruck,
  FiPhone, FiMail, FiX, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import '../styles/Customers.css';

// ─── Modal de Cliente ────────────────────────────────────────────────────────
const CustomerModal = ({ customer, onClose, onSaved }) => {
  const [form, setForm] = useState({
    customer_type: 'pf', name: '', document: '', email: '', phone: '', address: '', notes: '', status: 'ativo',
    ...(customer || {})
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (customer) {
        await api.put(`/api/customers/${customer.id}`, form);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/api/customers', form);
        toast.success('Cliente cadastrado com sucesso!');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{customer ? 'Editar Cliente' : 'Novo Cliente'}</h3>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Tipo de Pessoa *</label>
              <select value={form.customer_type} onChange={e => setForm({...form, customer_type: e.target.value})}>
                <option value="pf">Pessoa Física</option>
                <option value="pj">Pessoa Jurídica</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group flex-2">
              <label>Nome Completo / Razão Social *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nome do cliente" />
            </div>
            <div className="form-group">
              <label>{form.customer_type === 'pj' ? 'CNPJ' : 'CPF'} *</label>
              <input required value={form.document} onChange={e => setForm({...form, document: e.target.value})} placeholder={form.customer_type === 'pj' ? '00.000.000/0000-00' : '000.000.000-00'} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Telefone</label>
              <input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(00) 00000-0000" />
            </div>
            <div className="form-group flex-2">
              <label>E-mail</label>
              <input type="email" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@exemplo.com" />
            </div>
          </div>
          <div className="form-group">
            <label>Endereço</label>
            <input value={form.address || ''} onChange={e => setForm({...form, address: e.target.value})} placeholder="Rua, número, bairro, cidade" />
          </div>
          <div className="form-group">
            <label>Observações</label>
            <textarea rows={2} value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Observações adicionais..." />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Modal de Veículo ────────────────────────────────────────────────────────
const VehicleModal = ({ customerId, vehicle, onClose, onSaved }) => {
  const [form, setForm] = useState({ plate: '', model: '', color: '', notes: '', ...(vehicle || {}) });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (vehicle) {
        await api.put(`/api/customers/${customerId}/vehicles/${vehicle.id}`, form);
        toast.success('Veículo atualizado!');
      } else {
        await api.post(`/api/customers/${customerId}/vehicles`, form);
        toast.success('Veículo cadastrado!');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar veículo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{vehicle ? 'Editar Veículo' : 'Novo Veículo'}</h3>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Placa *</label>
            <input required value={form.plate} onChange={e => setForm({...form, plate: e.target.value.toUpperCase()})} placeholder="ABC1D23" maxLength={8} style={{textTransform:'uppercase', fontFamily:'monospace', fontSize:'18px', letterSpacing:'2px'}} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Modelo</label>
              <input value={form.model || ''} onChange={e => setForm({...form, model: e.target.value})} placeholder="Ex: Honda Civic" />
            </div>
            <div className="form-group">
              <label>Cor</label>
              <input value={form.color || ''} onChange={e => setForm({...form, color: e.target.value})} placeholder="Ex: Prata" />
            </div>
          </div>
          <div className="form-group">
            <label>Observações</label>
            <input value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Observações..." />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Card de Cliente ─────────────────────────────────────────────────────────
const CustomerCard = ({ customer, onEdit, onDelete, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);

  const loadVehicles = useCallback(async () => {
    if (!expanded) return;
    setLoadingVehicles(true);
    try {
      const res = await api.get(`/api/customers/${customer.id}/vehicles`);
      setVehicles(res.data.data || []);
    } catch (err) {
      toast.error('Erro ao carregar veículos');
    } finally {
      setLoadingVehicles(false);
    }
  }, [customer.id, expanded]);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const handleDeleteVehicle = async (vid) => {
    if (!window.confirm('Remover este veículo?')) return;
    try {
      await api.delete(`/api/customers/${customer.id}/vehicles/${vid}`);
      toast.success('Veículo removido');
      loadVehicles();
    } catch (err) {
      toast.error('Erro ao remover veículo');
    }
  };

  const statusColor = customer.status === 'ativo' ? '#22c55e' : '#ef4444';
  const typeLabel = customer.customer_type === 'pj' ? 'PJ' : 'PF';

  return (
    <div className={`customer-card ${customer.status === 'inativo' ? 'inactive' : ''}`}>
      <div className="customer-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="customer-info">
          <div className="customer-avatar">
            {customer.customer_type === 'pj' ? '🏢' : '👤'}
          </div>
          <div className="customer-details">
            <div className="customer-name-row">
              <span className="customer-name">{customer.name}</span>
              <span className="badge badge-type">{typeLabel}</span>
              <span className="badge" style={{background: statusColor + '20', color: statusColor}}>
                {customer.status}
              </span>
            </div>
            <div className="customer-meta">
              <span>📄 {customer.document}</span>
              {customer.phone && <span><FiPhone size={12} /> {customer.phone}</span>}
              {customer.email && <span><FiMail size={12} /> {customer.email}</span>}
            </div>
            <div className="customer-counts">
              <span className="count-badge"><FiTruck size={11} /> {customer.vehicle_count} veículo(s)</span>
              {customer.subscription_count > 0 && <span className="count-badge mensalista">Mensalista</span>}
              {customer.accredited_count > 0 && <span className="count-badge credenciado">Credenciado</span>}
            </div>
          </div>
        </div>
        <div className="customer-actions">
          <button className="btn-icon btn-edit" onClick={e => { e.stopPropagation(); onEdit(customer); }} title="Editar"><FiEdit2 /></button>
          <button className="btn-icon btn-delete" onClick={e => { e.stopPropagation(); onDelete(customer); }} title="Inativar"><FiTrash2 /></button>
          <button className="btn-icon btn-expand">{expanded ? <FiChevronUp /> : <FiChevronDown />}</button>
        </div>
      </div>

      {expanded && (
        <div className="customer-vehicles">
          <div className="vehicles-header">
            <h4><FiTruck /> Veículos Cadastrados</h4>
            <button className="btn-sm btn-primary" onClick={() => { setEditVehicle(null); setShowVehicleModal(true); }}>
              <FiPlus /> Adicionar Veículo
            </button>
          </div>
          {loadingVehicles ? (
            <p className="loading-text">Carregando...</p>
          ) : vehicles.length === 0 ? (
            <p className="empty-text">Nenhum veículo cadastrado</p>
          ) : (
            <div className="vehicles-list">
              {vehicles.map(v => (
                <div key={v.id} className={`vehicle-item ${!v.active ? 'inactive' : ''}`}>
                  <span className="vehicle-plate">{v.plate}</span>
                  <span className="vehicle-desc">{[v.model, v.color].filter(Boolean).join(' • ') || 'Sem descrição'}</span>
                  <div className="vehicle-actions">
                    <button className="btn-icon btn-edit" onClick={() => { setEditVehicle(v); setShowVehicleModal(true); }}><FiEdit2 /></button>
                    <button className="btn-icon btn-delete" onClick={() => handleDeleteVehicle(v.id)}><FiTrash2 /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showVehicleModal && (
        <VehicleModal
          customerId={customer.id}
          vehicle={editVehicle}
          onClose={() => setShowVehicleModal(false)}
          onSaved={() => { loadVehicles(); onRefresh(); }}
        />
      )}
    </div>
  );
};

// ─── Página Principal ────────────────────────────────────────────────────────
const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ativo');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      const res = await api.get('/api/customers', { params });
      setCustomers(res.data.data || []);
    } catch (err) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterType]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleDelete = async (customer) => {
    if (!window.confirm(`Inativar o cliente "${customer.name}"?`)) return;
    try {
      await api.delete(`/api/customers/${customer.id}`);
      toast.success('Cliente inativado');
      loadCustomers();
    } catch (err) {
      toast.error('Erro ao inativar cliente');
    }
  };

  const activeCount = customers.filter(c => c.status === 'ativo').length;
  const mensalistaCount = customers.filter(c => c.subscription_count > 0).length;
  const credenciadoCount = customers.filter(c => c.accredited_count > 0).length;

  return (
    <div className="customers-page">
      <div className="page-header">
        <div>
          <h1>👥 Clientes</h1>
          <p className="page-subtitle">Gestão de mensalistas, credenciados e seus veículos</p>
        </div>
        <button className="btn-primary" onClick={() => { setEditCustomer(null); setShowModal(true); }}>
          <FiPlus /> Novo Cliente
        </button>
      </div>

      {/* Resumo */}
      <div className="customers-summary">
        <div className="summary-card"><span className="summary-number">{activeCount}</span><span className="summary-label">Clientes Ativos</span></div>
        <div className="summary-card mensalista"><span className="summary-number">{mensalistaCount}</span><span className="summary-label">Mensalistas</span></div>
        <div className="summary-card credenciado"><span className="summary-number">{credenciadoCount}</span><span className="summary-label">Credenciados</span></div>
        <div className="summary-card total"><span className="summary-number">{customers.reduce((s, c) => s + parseInt(c.vehicle_count || 0), 0)}</span><span className="summary-label">Veículos Cadastrados</span></div>
      </div>

      {/* Filtros */}
      <div className="customers-filters">
        <div className="search-box">
          <FiSearch />
          <input
            placeholder="Buscar por nome, documento ou e-mail..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="ativo">Ativos</option>
          <option value="inativo">Inativos</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Todos os tipos</option>
          <option value="pf">Pessoa Física</option>
          <option value="pj">Pessoa Jurídica</option>
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="loading-state">Carregando clientes...</div>
      ) : customers.length === 0 ? (
        <div className="empty-state">
          <FiUser size={48} />
          <h3>Nenhum cliente encontrado</h3>
          <p>Cadastre o primeiro cliente clicando em "Novo Cliente"</p>
        </div>
      ) : (
        <div className="customers-list">
          {customers.map(c => (
            <CustomerCard
              key={c.id}
              customer={c}
              onEdit={cust => { setEditCustomer(cust); setShowModal(true); }}
              onDelete={handleDelete}
              onRefresh={loadCustomers}
            />
          ))}
        </div>
      )}

      {showModal && (
        <CustomerModal
          customer={editCustomer}
          onClose={() => setShowModal(false)}
          onSaved={loadCustomers}
        />
      )}
    </div>
  );
};

export default Customers;
